import type { IRepository } from '@infra/persistence/IRepository';
import {
  BusinessApiClient,
  BusinessApiConflictError,
  getBusinessApiClient,
} from '@infra/api/BusinessApiClient';

type VersionedEntity = { id: string; tenant_id: string };

function stripMeta<T extends VersionedEntity>(entity: T): Record<string, unknown> {
  const copy = { ...entity } as Record<string, unknown>;
  delete copy['id'];
  delete copy['tenant_id'];
  delete copy['_remoteVersion'];
  return copy;
}

function mergeRow<T extends VersionedEntity>(row: {
  id: string;
  tenant_id: string;
  payload: Record<string, unknown>;
  version: number;
}): T {
  return {
    ...(row.payload as object),
    id: row.id,
    tenant_id: row.tenant_id,
    _remoteVersion: row.version,
  } as unknown as T;
}

/** Repository HTTP vers nexxus-business-api pour une ressource (salaries, bulletins, ecritures). */
export class RemoteHttpEntityRepository<T extends VersionedEntity> implements IRepository<T> {
  private readonly versions = new Map<string, number>();

  constructor(
    private readonly resource: string,
    private readonly api: BusinessApiClient = getBusinessApiClient(),
  ) {}

  async findById(id: string): Promise<T | null> {
    const row = await this.api.getEntity(this.resource, id);
    if (!row) return null;
    this.versions.set(row.id, row.version);
    return mergeRow<T>(row);
  }

  async findAll(): Promise<T[]> {
    const rows = await this.api.listEntities(this.resource);
    for (const row of rows) this.versions.set(row.id, row.version);
    return rows.map((row) => mergeRow<T>(row));
  }

  async findBy(criteria: Partial<T>): Promise<T[]> {
    const all = await this.findAll();
    return all.filter((item) =>
      Object.entries(criteria).every(([k, v]) => (item as Record<string, unknown>)[k] === v),
    );
  }

  async save(entity: T): Promise<T> {
    const existing = await this.findById(entity.id);
    if (existing) {
      await this.update(entity);
      return entity;
    }
    await this.add(entity);
    return entity;
  }

  async saveMany(entities: T[]): Promise<T[]> {
    for (const e of entities) await this.save(e);
    return entities;
  }

  async add(entity: T): Promise<void> {
    const row = await this.api.createEntity(this.resource, stripMeta(entity));
    this.versions.set(row.id, row.version);
  }

  async update(entity: T): Promise<void> {
    const version =
      this.versions.get(entity.id) ??
      ((entity as Record<string, unknown>)['_remoteVersion'] as number | undefined) ??
      1;
    try {
      const row = await this.api.updateEntity(this.resource, entity.id, stripMeta(entity), version);
      this.versions.set(row.id, row.version);
    } catch (err) {
      if (err instanceof BusinessApiConflictError) throw err;
      throw err;
    }
  }

  async delete(id: string): Promise<void> {
    await this.api.deleteEntity(this.resource, id);
    this.versions.delete(id);
  }

  async count(criteria?: Partial<T>): Promise<number> {
    const items = criteria ? await this.findBy(criteria) : await this.findAll();
    return items.length;
  }

  async get(id: string): Promise<T | undefined> {
    return (await this.findById(id)) ?? undefined;
  }

  async list(): Promise<T[]> {
    return this.findAll();
  }
}
